<template>
    <section>
        <div class="px-4 pt-6 pb-12 mx-auto max-w-6xl bg-white xl:px-0">
            <div class="mx-auto max-w-5xl">
                <h4 class="mb-8 text-xl leading-5 text-center sm:text-2xl font-ww-bold md:text-4xl" v-html="mainHeading">
                </h4>
                <div class="mx-auto mb-10 text-base md:text-lg" v-html="description">
                </div>
            </div>
            <div class="flex flex-wrap gap-x-8 gap-y-8 justify-center lg:gap-x-16 md:gap-x-12 lg:gap-y-16">
                <div v-for="ingredient in ingredients" :key="ingredient.id" class="w-40 text-center md:w-48 lg:w-56">
                   <nuxt-img 
                        :src="ingredient.image" 
                        :width="imageWidth" 
                        :height="imageHeight" 
                        loading="lazy" 
                        sizes="xs:160px sm:160px md:160px lg:192px xl:217px"
                        :alt="ingredient.alt"
                        class="mx-auto mb-4 lg:mb-8" />
                    <h3 class="text-base text-left font-ww-bold md:text-17" v-html="ingredient.title"></h3>
                    <div class="text-base !leading-5 text-left md:text-17 lg:mt-6 mt-3 font-ww-regular" v-html="ingredient.description">
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
defineProps({
  mainHeading: {
    type: String,
    default: '6 Premium, Powerful Ingredients Combined to <br />Power Your Performance…All Day'
  },
  description: {
    type: String,
    default: 'What makes Mindflow so effective isn\'t just what\'s in it—it\'s how the six ingredients work together to create a balanced, high-performance energy experience.<br /><br />Each ingredient plays a specific role in enhancing focus, boosting mental stamina, and eliminating the common downsides of caffeine jitters, anxiety, or the afternoon crash.<br /><br />The result is clean, sustained energy that keeps your brain sharp and productive all day long.'
  },
  imageWidth: {
    type: Number,
    default: 217
  },
  imageHeight: {
    type: Number,
    default: 232
  },
  ingredients: {
    type: Array,
    default: () => [
      {
        id: 1,
        image: "/assets/images/organic-arabica.jpg",
        alt: "Organic Arabica Coffee",
        title: "Organic Arabica Coffee",
        description: "Delivers clean, natural caffeine without the sudden spike or jittery crash of synthetic caffeine."
      },
      {
        id: 2,
        image: "/assets/images/alpha-gpc.jpg",
        alt: "Alpha GPC", 
        title: "Alpha GPC",
        description: "Channels that stimulation into laser focus and memory by fueling the brain's key neurotransmitter, acetylcholine."
      },
      {
        id: 3,
        image: "/assets/images/lion-mane.jpg",
        alt: "Lion's Mane", 
        title: "Lion's Mane",
        description: "Supports neuroplasticity and cognitive clarity by stimulating nerve growth and protecting brain cells."
      },
      {
        id: 4,
        image: "/assets/images/gognitiq.jpg",
        alt: "Cognitiq", 
        title: "Cognitiq",
        description: "Increases BDNF (\"Miracle-Gro\" for the brain) to sharpen thinking and extend mental stamina."
      },
      {
        id: 5,
        image: "/assets/images/chaga.jpg",
        alt: "Chaga", 
        title: "Chaga",
        description: "Balances your energy and reduces inflammation to prevent overstimulation, fog, or fatigue."
      },
      {
        id: 6,
        image: "/assets/images/electropime.jpg",
        alt: "Electroprime", 
        title: "Electroprime",
        description: "Replenishes key electrolytes to offset caffeine-related dehydration, helping you stay hydrated, clear, and crash-free."
      },
      {
        id: 7,
        image: "/assets/images/grass-fed-callagen.jpg",
        alt: "Grass-Fed Collagen", 
        title: "Grass-Fed Collagen",
        description: "Strengthens the gut-brain axis and supports long-term mental clarity by reinforcing your body's neural and structural foundation—because focus starts from within."
      }
    ]
  }
})

defineBuilderComponent({
    friendlyName: 'Icon Feature Grid',
    canHaveChildren: false,
    inputs: [
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: '6 Premium, Powerful Ingredients Combined to <br />Power Your Performance…All Day'
        },
        {
            name: 'description',
            type: 'richText',
            defaultValue: 'What makes Mindflow so effective isn\'t just what\'s in it—it\'s how the six ingredients work together to create a balanced, high-performance energy experience.<br /><br />Each ingredient plays a specific role in enhancing focus, boosting mental stamina, and eliminating the common downsides of caffeine jitters, anxiety, or the afternoon crash.<br /><br />The result is clean, sustained energy that keeps your brain sharp and productive all day long.'
        },
        {
            name: 'imageWidth',
            type: 'number',
            defaultValue: 217
        },
        {
            name: 'imageHeight',
            type: 'number',
            defaultValue: 232
        },
        {
            name: 'ingredients',
            type: 'list',
            subFields: [
                {
                    name: 'id',
                    type: 'number'
                },
                {
                    name: 'image',
                    type: 'file'
                },
                {
                    name: 'alt',
                    type: 'text'
                },
                {
                    name: 'title',
                    type: 'richText'
                },
                {
                    name: 'description',
                    type: 'richText'
                }
            ],
            defaultValue: [
                {
                    id: 1,
                    image: "/assets/images/organic-arabica.jpg",
                    alt: "Organic Arabica Coffee",
                    title: "Organic Arabica Coffee",
                    description: "Delivers clean, natural caffeine without the sudden spike or jittery crash of synthetic caffeine."
                },
                {
                    id: 2,
                    image: "/assets/images/alpha-gpc.jpg",
                    alt: "Alpha GPC", 
                    title: "Alpha GPC",
                    description: "Channels that stimulation into laser focus and memory by fueling the brain's key neurotransmitter, acetylcholine."
                },
                {
                    id: 3,
                    image: "/assets/images/lion-mane.jpg",
                    alt: "Lion's Mane", 
                    title: "Lion's Mane",
                    description: "Supports neuroplasticity and cognitive clarity by stimulating nerve growth and protecting brain cells."
                },
                {
                    id: 4,
                    image: "/assets/images/gognitiq.jpg",
                    alt: "Cognitiq", 
                    title: "Cognitiq",
                    description: "Increases BDNF (\"Miracle-Gro\" for the brain) to sharpen thinking and extend mental stamina."
                },
                {
                    id: 5,
                    image: "/assets/images/chaga.jpg",
                    alt: "Chaga", 
                    title: "Chaga",
                    description: "Balances your energy and reduces inflammation to prevent overstimulation, fog, or fatigue."
                },
                {
                    id: 6,
                    image: "/assets/images/electropime.jpg",
                    alt: "Electroprime", 
                    title: "Electroprime",
                    description: "Replenishes key electrolytes to offset caffeine-related dehydration, helping you stay hydrated, clear, and crash-free."
                },
                {
                    id: 7,
                    image: "/assets/images/grass-fed-callagen.jpg",
                    alt: "Grass-Fed Collagen", 
                    title: "Grass-Fed Collagen",
                    description: "Strengthens the gut-brain axis and supports long-term mental clarity by reinforcing your body's neural and structural foundation—because focus starts from within."
                }
            ]
        }
    ]
})
</script>

<style scoped></style>